import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useEffect, useState, type ReactNode } from 'react'
import { z } from 'zod'
import { createClient } from '@kairos/supabase/browser'

import {
  ErrorBanner,
  Field,
  Stack,
  StatusCard,
  SubmitButton,
} from '~/components/auth/auth-ui'
import { logger } from '~/lib/logger'

/**
 * `/authentication/reset-password` — recovery sub-surface 4 from
 * `02-authentication.md`. Two visual variants:
 *
 *  - `form`    — new password + confirm password.
 *  - `expired` — destructive card linking back to /forgot-password.
 *
 * Supabase delivers the recovery session via the URL hash fragment
 * (`#access_token=…&type=recovery`). The `@supabase/ssr` browser client is
 * configured with `detectSessionInUrl: true`, which means it synchronously
 * picks the tokens off the hash and establishes the session — we just need
 * to wait for `onAuthStateChange('PASSWORD_RECOVERY')` (or, in practice,
 * `getSession()` after a tick) before letting the user submit.
 *
 * If we mount and there's no recovery session AND no hash, render the
 * expired variant. The `?state=expired` debug param forces the variant for
 * design review.
 */

const searchSchema = z.object({
  state: z.enum(['form', 'expired']).optional(),
})

const formSchema = z
  .object({
    password: z
      .string()
      .min(12, 'Use at least 12 characters')
      .regex(/[A-Z]/, 'Add an uppercase letter')
      .regex(/\d/, 'Add a number')
      .regex(/[^A-Za-z0-9]/, 'Add a symbol'),
    confirm: z.string(),
  })
  .refine((data) => data.password === data.confirm, {
    path: ['confirm'],
    message: 'Passwords do not match',
  })

type FormValues = z.infer<typeof formSchema>

export const Route = createFileRoute('/authentication/reset-password')({
  validateSearch: searchSchema,
  component: ResetPasswordPage,
})

type Variant = 'form' | 'expired' | 'success'

function ResetPasswordPage(): ReactNode {
  const navigate = useNavigate()
  const search = Route.useSearch()
  const [variant, setVariant] = useState<Variant>(search.state ?? 'form')
  const [bannerError, setBannerError] = useState<string | null>(null)

  // Detect the recovery session. If there's no session AND the URL has no
  // recovery hash, the link is expired/invalid.
  useEffect(() => {
    if (search.state === 'expired') {
      setVariant('expired')
      return
    }
    let cancelled = false
    void (async () => {
      const supabase = createClient()
      // Give detectSessionInUrl one tick to pick tokens off the hash.
      await new Promise((resolve) => setTimeout(resolve, 0))
      const { data } = await supabase.auth.getSession()
      const hash = window.location.hash || ''
      const looksLikeRecoveryHash = hash.includes('type=recovery') || hash.includes('access_token=')
      if (!cancelled && !data.session && !looksLikeRecoveryHash && search.state !== 'form') {
        setVariant('expired')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [search.state])

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { password: '', confirm: '' },
  })

  async function onSubmit(values: FormValues): Promise<void> {
    setBannerError(null)
    try {
      const supabase = createClient()
      const { error } = await supabase.auth.updateUser({ password: values.password })
      if (error) {
        if (error.status === 401 || error.status === 403) {
          setVariant('expired')
          return
        }
        setBannerError(error.message || 'We couldn&rsquo;t update your password. Try again.')
        return
      }
      // End other sessions (CASA 2.2.2).
      await supabase.auth.signOut({ scope: 'others' })
      setVariant('success')
    } catch (cause: unknown) {
      logger.warn('reset-password unexpected error', {
        message: cause instanceof Error ? cause.message : 'unknown',
      })
      setBannerError('Something went wrong. Try again.')
    }
  }

  if (variant === 'expired') {
    return (
      <StatusCard
        tone="destructive"
        glyph="!"
        title="This reset link is invalid or expired"
        body="Reset links expire 30 minutes after they're sent. Request a fresh one and try again."
      >
        <Link
          to="/authentication/forgot-password"
          className="ka-btn ka-btn--primary ka-btn--no-underline ka-auth-cta-mt"
        >
          Request a new link
        </Link>
      </StatusCard>
    )
  }

  if (variant === 'success') {
    return (
      <StatusCard
        tone="success"
        glyph="✓"
        title="Password updated"
        body="Sign in with your new password to continue."
      >
        <button
          type="button"
          onClick={() => { void navigate({ to: '/authentication/login' }) }}
          className="ka-btn ka-btn--primary ka-auth-cta-mt"
        >
          Continue to sign in
        </button>
      </StatusCard>
    )
  }

  return (
    <Stack>
      <header>
        <h1 className="ka-auth-h1">Set a new password</h1>
        <p className="ka-auth-sub">Choose something you don&rsquo;t use anywhere else.</p>
      </header>

      {bannerError ? <ErrorBanner message={bannerError} /> : null}

      <form noValidate onSubmit={handleSubmit(onSubmit)} method="post" className="ka-stack">
        <Field
          id="password"
          label="New password"
          type="password"
          autoComplete="new-password"
          placeholder="••••••••"
          error={errors.password?.message}
          {...register('password')}
        />
        <Field
          id="confirm"
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          placeholder="••••••••"
          error={errors.confirm?.message}
          {...register('confirm')}
        />
        <SubmitButton loading={isSubmitting} label="Save new password" loadingLabel="Saving…" />
      </form>

      <p className="ka-field-helper ka-auth-text-center">
        Need a fresh link?{' '}
        <Link to="/authentication/forgot-password" className="ka-link">
          Request a new one
        </Link>
      </p>
    </Stack>
  )
}
